import React from "react";
import { useParams } from "react-router-dom";

import PageContent from "ui/PageContent";
import Panel from "ui/Panel";

import { useDateRange } from "providers/WithDateRange";
import { Data, Datum, SeriesConfig } from "ui/Graph/util";
import Loading from "ui/Loading";
import DateRangeBar from "ui/DateRangeBar";
import GraphSection from "ui/Graph/GraphSection";
import { AreaSeries } from "ui/Graph/Series";
import DateRangeGraph from "ui/Graph/DateRangeGraph";

import Table from "./Table";
import {
  useServerGroupQueryPerformanceQuery,
  ServerGroupQueryPerformanceQuery,
} from "./gql/Query.generated";

function ServerGroupQueryPerformance() {
  const { serverGroupId } = useParams();
  const { from, to } = useDateRange();

  const { data, loading, error } = useServerGroupQueryPerformanceQuery({
    variables: {
      serverGroupId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <PageContent
      title="Query Performance"
      pageControls={<DateRangeBar />}
      pageCategory="queries"
      pageName="index_server"
    >
      <Panel title="Database Queries">
        <Graph data={data} />
        <Table
          data={data.getQueryOverviewStatsByDatabaseGroup.filter(
            (d) => !!d.databaseGroup,
          )}
        />
      </Panel>
    </PageContent>
  );
}

function Graph({ data }: { data: ServerGroupQueryPerformanceQuery }) {
  const noData = data.getQueryOverviewStatsByDatabaseGroup == null;
  const graphData: Data = {};
  const series: SeriesConfig[] = [];

  if (data?.getQueryOverviewStatsByDatabaseGroup) {
    data.getQueryOverviewStatsByDatabaseGroup.forEach((d) => {
      const key = d.databaseGroup?.id ?? "other";
      graphData[key] = d.queriesPerSecondSeries as unknown as Datum[];
      series.push({
        key: key,
        label: d.databaseGroup?.datname ?? "Other databases",
        type: AreaSeries,
      });
    });
  }

  return (
    <GraphSection noData={noData}>
      <DateRangeGraph
        data={graphData}
        axes={{
          left: {
            format: "count",
            tipFormat: (y) => y.toFixed(2) + " QPS",
          },
        }}
        series={series}
      />
    </GraphSection>
  );
}

export default ServerGroupQueryPerformance;
